import {
  faFile,
  faFileCsv,
  faFileExcel,
  faFileImage,
  faFilePdf,
  faFileWord,
  faTimes,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  CheckBadgeIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/20/solid"
import axios from "axios"
import moment from "moment"
import React from "react"
import { toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import { aricleCategories } from "../Content/HelpAndSupport"

import AudioIcon from "../components/common/file-icons/AudioIcon"
import { AvifFile } from "../components/common/file-icons/AvifFile"
import { BmpFile } from "../components/common/file-icons/BmpFile"
import { CsvIcon } from "../components/common/file-icons/CsvIcon"
import { DocFile } from "../components/common/file-icons/DocFile"
import { DocxFile } from "../components/common/file-icons/DocxFile"
import ExcelFile from "../components/common/file-icons/ExcelFile"
import { FilesIcon } from "../components/common/file-icons/FilesIcon"
import { GifFIle } from "../components/common/file-icons/GifFIle"
import { HeicFile } from "../components/common/file-icons/HeicFile"
import { HeifFile } from "../components/common/file-icons/HeifFile"
import { JPEGFile } from "../components/common/file-icons/JPEGFile"
import { MdbFile } from "../components/common/file-icons/MdbFile"
import { MsgFile } from "../components/common/file-icons/MsgFile"
import { OstFile } from "../components/common/file-icons/OstFile"
import { PdfFile } from "../components/common/file-icons/PdfFile"
import { PngFile } from "../components/common/file-icons/PngFile"
import { PptFile } from "../components/common/file-icons/PptFile"
import { PptxFIle } from "../components/common/file-icons/PptxFIle"
import { PstFile } from "../components/common/file-icons/PstFile"
import { QifFile } from "../components/common/file-icons/QifFile"
import { RtfFile } from "../components/common/file-icons/RtfFile"
import { TxtIcon } from "../components/common/file-icons/TxtIcon"
import VideoIcon from "../components/common/file-icons/VideoIcon"
import { VsdFile } from "../components/common/file-icons/VsdFile"
import { WebpFile } from "../components/common/file-icons/WebpFile"
import { XlsxFile } from "../components/common/file-icons/XlsxFile"
import { XmlIcon } from "../components/common/file-icons/XmlIcon"
import { ProtectedImg } from "../components/atoms/ProtectedImg"

export interface FileType {
  type?: string
  mimetype?: string
}

export type ToastType = "success" | "error"

export interface ImgProperties {
  url?: File
  type?: string
  sx?: string
  onClick?: () => void
}

export const stripHtmlTags = (str: string): string => {
  return str.replace(/<\/?[^>]+(>|$)/g, "")
}

// Toaster
export const toastHandler = (message: string, type: ToastType): void => {
  if (type === "success") {
    toast.success(message, {
      autoClose: 3000,
      style: {
        backgroundColor: "#193265",
        color: "white",
        borderRadius: "0.8rem",
      },
      closeButton: (
        <FontAwesomeIcon
          icon={faTimes}
          className={
            "w-4 !h-4 text-[9px] text-white bg-[#B695F8] rounded-[50%] p-[2px]"
          }
        />
      ),
      progressClassName: "custom-progress-bar",
      pauseOnFocusLoss: false,
      icon: <CheckBadgeIcon />,
    })
  } else if (type === "error") {
    toast.error(message, {
      autoClose: 3000,
      style: {
        backgroundColor: "#FFF5F5",
        color: "#991B1B",
        borderRadius: "0.8rem",
        border: "1px solid #ef171563",
      },
      closeButton: (
        <FontAwesomeIcon
          icon={faTimes}
          className={
            "w-4 !h-4 text-[9px] text-white bg-[#ef1715] rounded-[50%] p-[2px]"
          }
        />
      ),
      progressClassName: "custom-progress-bar",
      pauseOnFocusLoss: false,
      icon: <ExclamationTriangleIcon className="w-5 text-red-500" />,
    })
  }
}

// Function to get the file label and icon based on file type
export const getFileTypeLabel = (
  file: FileType
): { label: string; icon: any } => {
  // Match for PDF files
  if (file.type === "application/pdf" || file.mimetype === "application/pdf") {
    return { label: "PDF", icon: faFilePdf }
  } else if (
    file.type === "application/msword" ||
    file.type ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    file.mimetype === "application/msword" ||
    file.mimetype ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  ) {
    return { label: "Word Document", icon: faFileWord }
  } else if (
    file.type === "application/vnd.ms-excel" ||
    file.type ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    file.mimetype === "application/vnd.ms-excel" ||
    file.mimetype ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  ) {
    return { label: "Excel Spreadsheet", icon: faFileExcel }
  } else if (file.type === "text/csv" || file.mimetype === "text/csv") {
    return { label: "CSV", icon: faFileCsv }
  } else if (
    file.type.startsWith("image/") ||
    file.mimetype.startsWith("image/")
  ) {
    return { label: "Image", icon: faFileImage }
  } else {
    return { label: "File", icon: faFile }
  }
}

export const cleanSlug = (rawSlug?: string): string | null => {
  if (!rawSlug) return null
  return decodeURIComponent(rawSlug) // handle URL-encoded values
    .split("#")[0] // remove hash fragment
    .replace(/^\/|\/$/g, "") // trim leading/trailing slashes
    .toLowerCase()
}

// helper function
export const getParentSlugBySubSlug = (subSlug: string) => {
  for (const category of Object.values(aricleCategories)) {
    for (const subCategory of Object.values(category.subCategories)) {
      if (subCategory.slug === subSlug) return category
    }
  }
  return null
}

// getCsrf token function
export const getCsrfToken = async (): Promise<string | null> => {
  return localStorage.getItem("csrfToken")
}

// generate Csrf token function
export const generateCsrfToken = async (): Promise<string> => {
  const csrfResponse = await axios.get(
    `${process.env.GATSBY_SYNKLI_API_ENDPOINT}/auth/generate-csrf`,
    {
      withCredentials: true,
      headers: {
        app_environment: "business_website",
      },
    }
  )

  const csrfToken = csrfResponse?.data?.data?.csrfToken
  if (!csrfToken) throw new Error("CSRF token not available yet")

  localStorage.setItem("csrfToken", csrfToken)
  return csrfToken
}

export const getProfileFromStorage = (): any | null => {
  try {
    const stored = localStorage.getItem("synkli_profile_data")
    return stored ? JSON.parse(stored) : null
  } catch (error) {
    console.error("Error reading profile from localStorage:", error)
    return null
  }
}

export function formatDate({
  date,
  format = "DD MMM, YYYY",
}: {
  date?: string | Date
  format?: string
}): string | undefined {
  if (date) return moment(date).format(format)
}

export function formatTime(date?: string | Date): string {
  if (!date) return ""
  return moment.utc(date).local().format("h:mm A")
}

export const bytesToGB = (sizeInBytes: number): string => {
  var sizeInMB = sizeInBytes / 1024
  if (sizeInMB < 1) {
    return parseFloat(sizeInBytes.toString()) + "KB"
  } else if (sizeInMB >= 1 && sizeInMB < 1024) {
    return parseFloat(sizeInMB.toFixed(2)) + "MB"
  } else {
    var sizeInGB = sizeInMB / 1024.0
    return parseFloat(sizeInGB.toFixed(2)) + "GB"
  }
}

export const getFileExtension = (input?: string): string => {
  if (!input) return "unknown"
  if (input.includes("/")) return input.split("/").pop()!.toLowerCase()
  const parts = input.split(".")
  return parts.length > 1 ? parts.pop()!.toLowerCase() : "unknown"
}

export const getStatusColor = (status?: string) => {
  if (!status) {
    return "bg-[#B695F8] text-white "
  }

  if (status.includes("Unassigned")) return "!bg-[#B695F8] !text-white"
  if (status.includes("Open")) return "!bg-[#90EE90] !text-white"
  if (status.includes("Closed")) return "!bg-[red] !text-white"
}
export const getStatusTextColor = (status?: string) => {
  if (!status) {
    return "text-[black] "
  }
  if (status.includes("Unassigned")) return "!text-[#B695F8] "
  if (status.includes("Open")) return "!text-[#90EE90]"
  if (status.includes("Closed")) return "!text[red]"
}

export const truncateStringAtWord = (
  str: string,
  maxLength: number,
  ellipsis = "..."
): string => {
  if (typeof str !== "string") {
    return ""
  }

  if (str.length <= maxLength) {
    return str
  }

  const truncated = str.substring(0, maxLength)
  const lastSpaceIndex = truncated.lastIndexOf(" ")

  if (lastSpaceIndex > 0) {
    return truncated.substring(0, lastSpaceIndex) + ellipsis
  }

  return truncated + ellipsis
}

export const getFileIcon = (
  url?: string,
  sx = "",
  showImage = false,
  imgPrperties?: ImgProperties
) => {
  if (
    imgPrperties?.url &&
    showImage &&
    (imgPrperties?.type?.includes("jpg") ||
      imgPrperties?.type?.includes("png") ||
      imgPrperties?.type?.includes("jpeg"))
  ) {
    return (
      <ProtectedImg
        src={URL.createObjectURL(imgPrperties?.url)}
        alt={"alt" + url}
        onClick={imgPrperties?.onClick ? imgPrperties.onClick : () => {}}
        className={imgPrperties.sx || sx}
      />
    )
  }

  const fileExtension = url?.includes(".")
    ? url.split(".").pop()?.toLowerCase()
    : url?.split("/").pop()?.toLowerCase()
  if (url === "audio/mpeg") return <AudioIcon className={sx} />
  if (url === "video/mpeg") return <VideoIcon className={sx} />

  switch (fileExtension) {
    case "pdf":
      return <PdfFile className={sx} />
    case "mp3":
    case "wav":
    case "mpga":
    case "mpg":
    case "m4a":
    case "aac":
      return <AudioIcon className={sx} />
    case "mp4":
    case "avi":
    case "mov":
    case "wmv":
    case "x-msvideo":
    case "ogg":
    case "webm":
      return <VideoIcon className={sx} />

    case "msword":
    case "doc":
      return <DocFile className={sx} />
    case "docx":
      return <DocxFile className={sx} />
    case "txt":
      return <TxtIcon className={sx} />

    case "vsdx":
    case "vsd":
      return <VsdFile className={sx} />
    case "ost":
      return <OstFile className={sx} />
    case "pst":
      return <PstFile className={sx} />
    case "pptx":
      return <PptxFIle className={sx} />
    case "ppt":
      return <PptFile className={sx} />
    case "jpeg":
    case "jpg":
      return <JPEGFile className={sx} />

    case "gif":
      return <GifFIle className={sx} />

    case "qif":
      return <QifFile className={sx} />

    case "rtf":
      return <RtfFile className={sx} />

    case "bmp":
      return <BmpFile className={sx} />

    case "mdb":
      return <MdbFile className={sx} />
    case "xlsx":
      return <XlsxFile className={sx} />
    case "excel":
    case "vnd.ms-excel":
    case "xls":
      return <ExcelFile className={sx} />
    case "csv":
      return <CsvIcon className={sx} />
    case "xml":
      return <XmlIcon className={sx} />
    case "png":
      return <PngFile className={sx} />

    case "heic":
      return <HeicFile className={sx} />
    case "heif":
      return <HeifFile className={sx} />
    case "webp":
      return <WebpFile className={sx} />
    case "avif":
      return <AvifFile className={sx} />

    case "ms-outlook":
    case "rfc822":
    case "msg":
      // case 'octet-stream':
      return <MsgFile className={sx} />
    default:
      return (
        <div className={sx}>
          <FilesIcon className={"h-auto m-auto stroke-white"} />
        </div>
      )
  }
}

export const getRemoteAccessChipTitles = (status: string) => {
  if (!status) {
    return ""
  }
  if (status === "rejected") {
    return "Denied"
  }
  if (status === "approved") {
    return "Access granted"
  }
  if (status === "cancelled") {
    return "Access removed"
  }
  if (status === "closed") {
    return "Access closed"
  }
  if (status === "used") {
    return "Accessing the firm"
  }
}

export const getRemoteAccessChipColor = (status?: string) => {
  if (!status) {
    return ""
  }
  if (status === "rejected") {
    return "!bg-red-500"
  }
  if (status === "closed") {
    return "!bg-red-500"
  }
  if (status === "approved") {
    return "!bg-[#5EDAA6]"
  }
  if (status === "cancelled") {
    return "!bg-red-500"
  }
  if (status === "used") {
    return "!bg-[#5EDAA6]"
  }
}
export const hasRemoteAccessButton = (status?: string) => {
  if (!status) {
    return false
  }
  if (status === "rejected") {
    return false
  }
  if (status === "approved") {
    return true
  }
  if (status === "closed") {
    return false
  }
  if (status === "cancelled") {
    return false
  }
  if (status === "used") {
    return true
  }
  return false
}

export const removeHtmlTags = (str?: string): string => {
  if (!str) {
    return ""
  }
  // Define all standard HTML tags
  const htmlTags = [
    // Document structure
    "html",
    "head",
    "body",
    "title",
    "meta",
    "link",
    "style",
    "script",
    "noscript",

    // Text content
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "p",
    "div",
    "span",
    "pre",
    "code",
    "samp",
    "kbd",
    "var",

    // Text formatting
    "strong",
    "b",
    "em",
    "i",
    "u",
    "s",
    "small",
    "sub",
    "sup",
    "mark",
    "del",
    "ins",

    // Lists
    "ul",
    "ol",
    "li",
    "dl",
    "dt",
    "dd",

    // Links and media
    "a",
    "img",
    "video",
    "audio",
    "source",
    "track",
    "embed",
    "object",
    "param",
    "iframe",

    // Tables
    "table",
    "thead",
    "tbody",
    "tfoot",
    "tr",
    "th",
    "td",
    "caption",
    "colgroup",
    "col",

    // Forms
    "form",
    "input",
    "textarea",
    "select",
    "option",
    "optgroup",
    "button",
    "label",
    "fieldset",
    "legend",

    // Semantic elements
    "header",
    "nav",
    "main",
    "section",
    "article",
    "aside",
    "footer",
    "figure",
    "figcaption",

    // Other common tags
    "br",
    "hr",
    "blockquote",
    "q",
    "cite",
    "abbr",
    "dfn",
    "time",
    "address",
    "details",
    "summary",
    "dialog",
    "menu",
    "menuitem",
    "canvas",
    "svg",
    "math",
    "progress",
    "meter",
    "output",

    // Less common but valid tags
    "area",
    "base",
    "bdi",
    "bdo",
    "wbr",
    "rp",
    "rt",
    "ruby",
    "template",
    "slot",
  ]

  // Create regex pattern for opening and closing tags
  const tagPattern = htmlTags
    .map(tag => `</?${tag}(?:\\s[^>]*)?>|<${tag}(?:\\s[^>]*)?/>`)
    .join("|")
  const regex = new RegExp(tagPattern, "gi")

  // Remove only the defined HTML tags
  return str.replace(regex, "")
}

export const getInitials = (firstName = "", lastName = "") => {
  const firstInitial = firstName?.[0] || ""
  const lastInitial = lastName?.[0] || ""
  return (firstInitial + lastInitial).toUpperCase()
}
